import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag, User, Search, Heart, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';
import { useSEO } from '@/utils/seo';
import WhatsAppFAB from '@/components/common/WhatsAppFAB';
import Footer from '@/components/layout/Footer';

const navLinks = [
  { name: 'Accessories', href: '/shop/accessories', hasDropdown: true, items: [
    { name: 'Scarves', href: '/shop/accessories?type=scarves' },
    { name: 'Wallets', href: '/shop/accessories?type=wallets' },
    { name: 'Shawls', href: '/shop/accessories?type=shawls' },
  ]},
  { name: 'Art & Decor', href: '/shop/home-living', hasDropdown: true, items: [
    { name: 'Flowers', href: '/shop/home-living?type=flowers' },
    { name: 'Art', href: '/shop/home-living?type=art' },
  ]},
  { name: 'Gourmet', href: '/shop/gourmet' },
  { name: 'Experiences', href: '/shop/experiences' },
  { name: 'Gift Cards', href: '/gifting' },
  { name: 'Limited Editions', href: '/shop/limited-editions' },
  { name: 'About', href: '/about', hasDropdown: true, items: [
    { name: 'Journal', href: '/journal' },
    { name: 'The Craft', href: '/the-craft' },
    { name: 'Impact', href: '/impact' },
  ]},
];

const Layout = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { getCartCount } = useCart();
  const { user, logout } = useAuth();
  const { currency, currencies, changeCurrency, symbol } = useCurrency();

  // Global SEO hook — sets title/meta/canonical/JSON-LD per route on every navigation.
  // Product & journal pages call setProductSEO / setJournalSEO after fetch to override.
  useSEO();

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setIsSearchOpen(false);
    setSearchQuery('');
    navigate(`/shop?search=${encodeURIComponent(q)}`);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isHomePage = location.pathname === '/';
  const headerBg = isScrolled || !isHomePage 
    ? 'bg-background/95 backdrop-blur-md border-b border-border/50' 
    : 'bg-transparent';
  const textColor = isScrolled || !isHomePage ? 'text-foreground' : 'text-foreground';

  return (
    <div className="min-h-screen grain-texture">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}>
        {/* Top Bar */}
        <div className="hidden lg:block border-b border-border/30">
          <div className="chaleur-container flex justify-between items-center py-2 text-xs">
            <p className="text-muted-foreground">Free shipping on orders above ₹5,000 in India</p>
            <div className="flex items-center gap-6">
              <Link to="/corporate-enquiries" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="topbar-corporate">
                Corporate Enquiries
              </Link>
              <Link to="/become-a-partner" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="topbar-become-partner">
                Partners
              </Link>
              <Link to="/chaleur-club" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="topbar-collective">
                The Chaleur Collective
              </Link>
              <Link to="/track-order" className="text-muted-foreground hover:text-foreground transition-colors">
                Track Order
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
                  {symbol} {currency} <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-none border-border">
                  {Object.entries(currencies).map(([code, { symbol, name }]) => (
                    <DropdownMenuItem 
                      key={code}
                      onClick={() => changeCurrency(code)}
                      className={`cursor-pointer ${currency === code ? 'bg-secondary' : ''}`}
                    >
                      {symbol} {code} - {name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <div className="chaleur-container">
          <div className="flex items-center justify-between h-20">
            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img 
                src="/images/brand/chaleur-logo-clean.png" 
                alt="Chaleur" 
                className="h-10 md:h-12 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                link.hasDropdown ? (
                  <DropdownMenu key={link.name}>
                    <DropdownMenuTrigger className={`flex items-center gap-1 text-sm uppercase tracking-[0.1em] ${textColor} hover:text-primary transition-colors link-hover`}>
                      {link.name} <ChevronDown className="w-3 h-3" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="rounded-none border-border min-w-[180px]">
                      {link.items.map((item) => (
                        <DropdownMenuItem key={item.href} asChild>
                          <Link to={item.href} className="cursor-pointer">
                            {item.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link 
                    key={link.name}
                    to={link.href}
                    className={`text-sm uppercase tracking-[0.1em] ${textColor} hover:text-primary transition-colors link-hover`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsSearchOpen(true)}
                className="p-2 hover:text-primary transition-colors"
                aria-label="Search"
                data-testid="open-search-btn"
              >
                <Search className="w-5 h-5" />
              </button>
              
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger className="p-2 hover:text-primary transition-colors">
                    <User className="w-5 h-5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-none border-border min-w-[180px]">
                    <div className="px-3 py-2 border-b border-border">
                      <p className="text-sm font-medium">{user.first_name}</p>
                      <p className="text-xs text-muted-foreground">{user.loyalty_points} points</p>
                    </div>
                    <DropdownMenuItem asChild>
                      <Link to="/account" className="cursor-pointer">My Account</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/account?tab=orders" className="cursor-pointer">Orders</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/chaleur-club" className="cursor-pointer">Chaleur Club</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive">
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/login" className="p-2 hover:text-primary transition-colors">
                  <User className="w-5 h-5" />
                </Link>
              )}

              <Link to="/cart" className="p-2 hover:text-primary transition-colors relative">
                <ShoppingBag className="w-5 h-5" />
                {getCartCount() > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs flex items-center justify-center">
                    {getCartCount()}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-background border-t border-border">
            <nav className="chaleur-container py-6 space-y-4">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.hasDropdown ? (
                    <div className="space-y-2">
                      <p className="text-sm uppercase tracking-[0.1em] text-muted-foreground">{link.name}</p>
                      {link.items.map((item) => (
                        <Link 
                          key={item.href}
                          to={item.href}
                          className="block pl-4 py-2 text-sm hover:text-primary transition-colors"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link 
                      to={link.href}
                      className="block py-2 text-sm uppercase tracking-[0.1em] hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t border-border space-y-2">
                <Link to="/corporate-enquiries" className="block py-2 text-sm text-muted-foreground hover:text-foreground" data-testid="mobile-corporate">
                  Corporate Enquiries
                </Link>
                <Link to="/become-a-partner" className="block py-2 text-sm text-muted-foreground hover:text-foreground">
                  Partners
                </Link>
                <Link to="/chaleur-club" className="block py-2 text-sm text-muted-foreground hover:text-foreground">
                  The Chaleur Collective
                </Link>
                <Link to="/track-order" className="block py-2 text-sm text-muted-foreground hover:text-foreground">
                  Track Order
                </Link>
                <Link to="/contact" className="block py-2 text-sm text-muted-foreground hover:text-foreground">
                  Contact
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Search Overlay */}
      {isSearchOpen && (
        <div
          className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-md flex items-start justify-center pt-32"
          data-testid="search-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setIsSearchOpen(false); }}
        >
          <div className="chaleur-container max-w-2xl w-full">
            <form onSubmit={handleSearchSubmit} className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                autoFocus
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search scarves, wraps, artisans..."
                className="rounded-none h-14 pl-12 pr-14 text-base border-t-0 border-x-0 border-b-2 border-foreground bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="search-input"
              />
              <button
                type="button"
                onClick={() => setIsSearchOpen(false)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Close search"
                data-testid="close-search-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </form>
            <p className="text-xs text-muted-foreground mt-3 tracking-wider uppercase">Press Enter to search</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <Footer />

      {/* WhatsApp FAB */}
      <WhatsAppFAB />
    </div>
  );
};

export default Layout;
